import dayjs from 'dayjs';
import Big from "big.js";
import _ from "underscore";
import {CustomFieldHelper} from "bukku/shared/CustomFieldHelper";
import {FormItemHelper} from "bukku/components/Editable/FormItem/helper";
import {TermItemHelper} from "bukku/components/Editable/PaymentTerm/helper";
import {TransactionFileHelper} from "../../../../components/LightGallery/TransactionFilesLightGallery/helper";
import {Type} from "../../../../redux/Transaction/helper";
import {message} from "antd";
import {t} from "@lingui/macro";
import { DepositItemHelper } from "../../../../components/Editable/PaymentDepositItem/helper";
import { SubscriptionCheck } from '../../../../components/Access/helper/subscription/SubscriptionCheck';
import { SubscriptionScopeKey } from '../../../../components/Access/helper/subscription/enum/SubscriptionScopeKey';
import { ContactPersonsHelper } from '../../Contacts/ContactPersons/helper';
import { readAutosaveStorage } from '../../../../redux/LocalStorage/helpers/AutosaveTransactions/readStorage';
import { AutosaveHelper } from "../../../../components/Autosave/helper";
import { store } from "bukku/redux/store";

export const Helper = {
    // Normalise data for comparison.
    normalizeData: (data, formItems, termItems, depositItems,
        appliedLinkIds, files, isConsolidated = false) => {
        const { Auth, List } = store.getState();
        const isMalaysianCompany = Auth.companyCountryCode === 'MY';
        const myInvoisSubmissionOn = List.settings.is_myinvois_submission_enabled;

        const hasContactGrpSub = SubscriptionCheck
            .withKeys(SubscriptionScopeKey['CONTACT.GROUP']).hasSubscriptionTo();

        // const hasMultiCurrencySub =
        // SubscriptionCheck.withKeys(SubscriptionScopeKey.MULTI_CURRENCY).hasSubscriptionTo();

        const hasCustomFieldSub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.CUSTOM_FIELD).hasSubscriptionTo();

        const hasTagSub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.TAG).hasSubscriptionTo();

        const newData = {
            payment_mode: data.payment_mode || null,
            contact_id: data.contact_id || null,
            group_id: !hasContactGrpSub ? undefined : data.group_id || null,
            title: data.title || null,
            billing_party: data.billing_party || null,

            billing_contact_person_id: data.billing_contact_person_id || null,
            billing_contact_person: data.billing_contact_person || null,
            show_shipping: data.show_shipping || null,
            shipping_info: data.shipping_info || null,
            shipping_party: data.shipping_party || null,
            shipping_contact_person_id: data.shipping_contact_person_id || null,
            shipping_contact_person: data.shipping_contact_person || null,
            number: data.number || null,
            number2: data.number2 || null,
            date: data.date || null,
            currency_code: data.currency_code || null,
            exchange_rate: data.exchange_rate ? (parseFloat(data.exchange_rate) || 1) : null,
            tax_mode: data.tax_mode || null,
            form_items: [],
            rounding_on: data.rounding_on || null,

            term_items: [],
            description: data.description || null,
            internal_note: data.internal_note || null,
            remarks: data.remarks || null,
            tag_ids: hasTagSub ? data.tag_ids || null : undefined,
            files: [],
            linked_transaction_ids: appliedLinkIds || [],
            status: data.status,
            is_consolidated: isConsolidated || false,
        };

        // myInvois fields (MY companies only)
        if (isMalaysianCompany) {
            // myinvois_action only if the myinvois_submission setting is turned on, and transaction will be created as ready
            // ('transaction_status' is used for recurring instructions)
            if (myInvoisSubmissionOn && (newData.status === 'ready' || data.transaction_status === 'ready'))
                newData.myinvois_action = data.myinvois_action || null;

            newData.customs_form_no = data.customs_form_no || null;
            newData.customs_k2_form_no = data.customs_k2_form_no || null;
            newData.incoterms = data.incoterms || null;
        }

        newData.form_items = FormItemHelper.normalizeData(formItems);
        if (data.payment_mode !== 'cash')
            TermItemHelper.normalizeData(newData, termItems);
        if (data.payment_mode !== 'credit')
            DepositItemHelper.normalizeData(newData, depositItems);

        if (hasCustomFieldSub)
            CustomFieldHelper.normalizeData(newData, data.fields);
        TransactionFileHelper.normalizeData(newData, files);
        return newData;
    },

    getInitFormData: initTransaction => {
        const formData = {
            // General
            payment_mode: initTransaction.payment_mode,
            contact_id: initTransaction.contact_id,
            group_id: initTransaction.group_id,
            title: initTransaction.title,
            billing_party: initTransaction.billing_party,
            billing_contact_person_id_or_name: initTransaction.billing_contact_person_id_or_name,
            shipping_info: initTransaction.shipping_info,
            shipping_party: initTransaction.shipping_party,
            shipping_contact_person_id_or_name: initTransaction.shipping_contact_person_id_or_name,
            number: initTransaction.number,
            number2: initTransaction.number2,
            date: initTransaction.date ? dayjs(initTransaction.date) : undefined,
            currency_code: initTransaction.currency_code,
            exchange_rate: initTransaction.exchange_rate,
            tag_ids: initTransaction.tag_ids,

            // Item
            tax_mode: initTransaction.tax_mode,
            rounding_on: initTransaction.rounding_on,

            // Others
            remarks: initTransaction.remarks,
            description: initTransaction.description,
            internal_note: initTransaction.internal_note,
            status: initTransaction.status,

            // myInvois fields (MY companies only)
            myinvois_action: initTransaction.myinvois_action,
            customs_form_no: initTransaction.customs_form_no,
            customs_k2_form_no: initTransaction.customs_k2_form_no,
            incoterms: initTransaction.incoterms,

            // Fields
            ...CustomFieldHelper.initFormData(initTransaction),
        };

        return formData;
    },

    prepareFormData: (rawFormData, showShipping, billingContactPersonId, shippingContactPersonId) => {
        // if an existing cp is selected, pass in X_contact_person_id; otherwise if it's free text, pass in X_contact_person
        const billing_cp_name = billingContactPersonId ? 'billing_contact_person_id' : 'billing_contact_person';
        const shipping_cp_name = shippingContactPersonId ? 'shipping_contact_person_id' : 'shipping_contact_person';

        return (
            {
                payment_mode: rawFormData.payment_mode,
                contact_id: rawFormData.contact_id,
                group_id: rawFormData.group_id,
                title: rawFormData.title,
                billing_party: rawFormData.billing_party,
                [billing_cp_name]: billingContactPersonId || rawFormData.billing_contact_person_id_or_name,
                show_shipping: showShipping,
                shipping_info: rawFormData.shipping_info,
                shipping_party: rawFormData.shipping_party,
                [shipping_cp_name]: shippingContactPersonId || rawFormData.shipping_contact_person_id_or_name,
                number: rawFormData.number,
                number2: rawFormData.number2,
                date: rawFormData.date?.format("YYYY-MM-DD"),
                currency_code: rawFormData.currency_code,
                exchange_rate: rawFormData.exchange_rate,
                tag_ids: rawFormData.tag_ids,
                tax_mode: rawFormData.tax_mode,
                form_items: [],
                rounding_on: rawFormData.rounding_on,

                term_items: [],
                description: rawFormData.description,
                internal_note: rawFormData.internal_note,
                fields: [],
                remarks: rawFormData.remarks,
                status: rawFormData.status,
                transaction_status: rawFormData.transaction_status, // for recurring instructions

                // myInvois fields (MY companies only)
                myinvois_action: rawFormData.myinvois_action,
                customs_form_no: rawFormData.customs_form_no,
                customs_k2_form_no: rawFormData.customs_k2_form_no,
                incoterms: rawFormData.incoterms,

                // Fields
                ...CustomFieldHelper.prepareFormData(rawFormData, Type.sale_invoice),
            }
        );
    },

    isModified: (transaction, formData, formItems, termItems,
        depositItems, appliedLinkIds, files, appliedLinkItems, autosaveProps = {}) => {
        const {
            checkAutosave: isAutosave = false,
            // currentTime: lastSavedTime = null,
            // isWindowUnload = false,
            formSummaryData = {},
            // isOnPageChange,
            // hasAnyChange,
            // action,
        } = autosaveProps;

        const { data } = readAutosaveStorage(Type.sale_invoice) || {};

        const initData = (data && isAutosave) ? data : transaction;
        let initialData = Helper.normalizeData(
            initData,
            initData.form_items,
            initData.term_items,
            initData.deposit_items || [],
            initData.linked_transaction_ids || [],
            initData.files,

        );

        const currentData = Helper.normalizeData(
            formData, formItems, termItems, depositItems, appliedLinkIds, files,
        );

        initialData = ContactPersonsHelper.normalizeContactPersonsData(
            initialData, currentData,
        );

        const hasChanges = !_.isEqual(initialData, currentData);

        // for view.js
        if (!isAutosave)
            return hasChanges;

        const storageProps = {
            currentData,
            files,
            additionalProps: {
                form_summary_total: formSummaryData?.total,
                linked_items: appliedLinkItems,
            },
            formItems,
            depositItems,
        };
        return AutosaveHelper.isModified(
            Type.sale_invoice, data, hasChanges, autosaveProps, storageProps);
    },

    isPaymentScheduleComplete: (formSummaryData, paymentMode, termItems) => {
        let paymentTotal = new Big(0);
        if (paymentMode === 'cash')
            return true;

        const invoiceTotal = formSummaryData.total;
        if (!termItems || !termItems.length) {
            message.warning(t`Please add at least one payment schedule.`);
            return false;
        }

        termItems.forEach(item => {
            paymentTotal = paymentTotal.plus(item.amount);
        });

        const isComplete = !!paymentTotal.eq(invoiceTotal);
        if (!isComplete) {
            const amountText = parseFloat(new Big(invoiceTotal).minus(paymentTotal));
            message.warning(
                t`Please complete the payment schedule following the invoice total. (Missing ${amountText})`,
            );
        }

        return isComplete;
    },

    hasIncompleteItems: (formItems, termItems) => {
        let hasError = false;

        formItems.forEach(item => {
            if (item.dirty)
                hasError = true;
            // check bundle items
            if (item.children && Helper.hasIncompleteItems(item.children, []))
                hasError = true;
            if (item.type === 'subtitle' && !item.description)
                hasError = true;
        });

        termItems.forEach(item => {
            if (!item.term_id && !item.date)
                hasError = true;
            if (item.dirty)
                hasError = true;
        });
        return hasError;
    },

    // Use term settings to determine due date, return date string.
    calcDueDateByTerm: (term, date) => {
        if (date) {
            const dateObj = dayjs(date, "YYYY-MM-DD");
            switch (term?.type) {
                case 'in_days':
                    return dateObj.add(term.value, 'days').format("YYYY-MM-DD");
                case 'day_of_month':
                    if (dateObj.date() >= term.value)
                        return dateObj.date(term.value).add(1, 'month').format("YYYY-MM-DD");
                    return dateObj.date(term.value).format("YYYY-MM-DD");
                case 'day_of_following_month':
                    return dateObj.date(term.value).add(1, 'month').format("YYYY-MM-DD");
                case 'end_of_month':
                    return dateObj.date(1).add(term.value, 'month').endOf('month').format("YYYY-MM-DD");
                case 'days_after_month':
                    return dateObj.endOf('month').add(term.value, 'days').format("YYYY-MM-DD");
                default:
                    return null;
            }
        }
        return null;
    },
};
